import React, { useState } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { TouchButton } from './TouchInput';

/**
 * Form Wizard/Stepper Component for Multi-Step Forms
 * Mobile-optimized with progress indicator
 */
const FormWizard = ({ steps, onComplete, onCancel }) => {
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleNext = async () => {
    // Validate current step if validation function provided
    if (currentStepData.validate) {
      const isValid = await currentStepData.validate(formData);
      if (!isValid) return;
    }

    if (isLastStep) {
      // Submit form
      onComplete(formData);
    } else {
      // Go to next step
      setCurrentStep(currentStep + 1);
      // Scroll to top on mobile
      if (isMobile) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const updateFormData = (data) => {
    setFormData({ ...formData, ...data });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: isMobile ? '100vh' : 'auto'
      }}
    >
      {/* Progress Bar */}
      <div
        style={{
          position: isMobile ? 'sticky' : 'relative',
          top: 0,
          background: 'var(--bg-surface-elevated)',
          padding: isMobile ? '16px 20px' : '20px 24px',
          borderBottom: '1px solid var(--border-dim)',
          zIndex: 2
        }}
      >
        {/* Step indicator */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}
        >
          <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
            {currentStepData.title}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Krok {currentStep + 1} z {steps.length}
          </div>
        </div>

        {/* Progress bar */}
        <div
          style={{
            width: '100%',
            height: '4px',
            background: 'var(--border-dim)',
            borderRadius: 'var(--radius-full)',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: '100%',
              background: 'var(--gold)',
              transition: 'width 0.3s ease',
              borderRadius: 'var(--radius-full)'
            }}
          />
        </div>
      </div>

      {/* Step Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: isMobile ? '20px' : '24px',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {currentStepData.description && (
          <p
            style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              marginBottom: '24px',
              lineHeight: 1.6
            }}
          >
            {currentStepData.description}
          </p>
        )}

        {/* Render current step component */}
        {currentStepData.component({ formData, updateFormData })}
      </div>

      {/* Navigation Buttons - Sticky on mobile */}
      <div
        style={{
          position: isMobile ? 'sticky' : 'relative',
          bottom: 0,
          background: 'var(--bg-surface-elevated)',
          padding: isMobile ? '16px 20px' : '20px 24px',
          borderTop: '1px solid var(--border-dim)',
          display: 'flex',
          gap: '12px',
          zIndex: 2
        }}
      >
        {onCancel && isFirstStep && (
          <TouchButton variant="secondary" onClick={onCancel} style={{ flex: 1 }}>
            Anuluj
          </TouchButton>
        )}

        {!isFirstStep && (
          <TouchButton variant="secondary" onClick={handlePrevious} style={{ flex: 1 }}>
            ← Wstecz
          </TouchButton>
        )}

        <TouchButton variant="primary" onClick={handleNext} style={{ flex: 2 }}>
          {isLastStep ? '✓ Wyślij formularz' : 'Dalej →'}
        </TouchButton>
      </div>
    </div>
  );
};

export default FormWizard;
